import { GambytUser } from '@Components/users/types'
import { NavigateFunction, NonIndexRouteObject, UIMatch } from 'react-router-dom'
import { AuthenticatedApp } from '../../AuthenticatedApp'
import { AuthScopes, FeatureFlag } from '@Generated/back-office-client/src'
import { NotFound } from '@Components/not-found'
import { getRouteList, RouteGroup } from './route-list'
import { DropdownMenu, Text } from '@radix-ui/themes'
import { Routes } from './types'

export type BaseRouteConfig = {
  routerConfig: NonIndexRouteObject
}

export type RouteConfig = BaseRouteConfig & {
  scope?: AuthScopes
  scopes?: AuthScopes[]
  feature?: FeatureFlag
  gambytAdminOnly?: boolean
  childrenRoutes?: ChildRouteConfig[]
}

export type ParentRouteConfig = RouteConfig & {
  routeGroup: RouteGroup
}

export type ChildRouteConfig = RouteConfig & {
  submenuDisplayName?: string
  submenuRoute?: Routes
}

const baseRoute: BaseRouteConfig = {
  routerConfig: {
    path: '/',
    Component: AuthenticatedApp,
    children: [{ path: '*', Component: NotFound }],
  },
}

/**
 * Recursivly traverse all routes in the route tree and filter out the ones that we aren't allowed to access
 */
const getAccessibleRoutes = (route: RouteConfig, user: GambytUser) => {
  if (route.gambytAdminOnly && !user.isGambytAdmin) {
    return null
  }

  const canAccessRouteSingularScope = route.scope ? user.scopes.includes(route.scope) : true
  const canAccessRouteMultipleScopes = route.scopes?.length
    ? route.scopes.some((scope) => user.scopes.includes(scope))
    : canAccessRouteSingularScope

  const hasRequiredFeature = route.feature ? user.enabledFeatures.includes(route.feature) : true

  if ((canAccessRouteSingularScope || canAccessRouteMultipleScopes) && hasRequiredFeature) {
    if (route.childrenRoutes) {
      const accessibleRoutes = route.childrenRoutes.filter((cr) => getAccessibleRoutes(cr, user))
      const routeWithChildren: RouteConfig = {
        ...route,
        childrenRoutes: accessibleRoutes,
      }
      return routeWithChildren
    }

    return route
  }
}

export const getRouteConfigForUser = (
  user: GambytUser | null,
): BaseRouteConfig[] | RouteConfig[] => {
  if (!user) {
    return [baseRoute]
  }

  const appRoutes = getRouteList().reduce((acc: RouteConfig[], route) => {
    const accessibleRoutes = getAccessibleRoutes(route, user)
    if (accessibleRoutes) {
      acc = [...acc, accessibleRoutes]
    }

    return acc
  }, [])

  return appRoutes
}

/**
 * Recursivly traverse all routes that we were able to allow access for above and transform them into an object
 * that is specific to react-router
 */
export const getRouteObjectForUser = (routeConfig: RouteConfig) => {
  if (routeConfig.childrenRoutes) {
    const routeWithChildren: NonIndexRouteObject = {
      ...routeConfig.routerConfig,
      children: routeConfig.childrenRoutes
        ? routeConfig.childrenRoutes.map((cr) => getRouteObjectForUser(cr))
        : undefined,
    }

    return routeWithChildren
  }

  return routeConfig.routerConfig
}

export const getRoutesForUser = (user: GambytUser | null) => {
  const routeConfigs = getRouteConfigForUser(user)

  const appRoutes = routeConfigs.reduce((acc: NonIndexRouteObject[], route) => {
    acc = [...acc, getRouteObjectForUser(route)]

    return acc
  }, [])

  return [
    {
      ...baseRoute.routerConfig,
      children: [...appRoutes, ...(baseRoute.routerConfig.children ?? [])],
    },
  ]
}

export const getNavMenu = (
  routeMatches: UIMatch[],
  routes: (BaseRouteConfig | ParentRouteConfig)[],
  navigate: NavigateFunction,
) => {
  if (!routes) {
    return null
  }

  const routeGroupings = routes.reduce(
    (acc, route) => {
      if (!('routeGroup' in route)) {
        return acc
      }

      if (route.routeGroup && acc[route.routeGroup]) {
        acc[route.routeGroup].push(route)
      } else if (route.routeGroup) {
        acc[route.routeGroup] = [route]
      }

      return acc
    },
    {} as Record<RouteGroup, Array<RouteConfig>>,
  )

  return Object.keys(routeGroupings)
    .map((routeGroup) => {
      const routeGroupConfigs = routeGroupings[routeGroup as RouteGroup]

      const submenuRoutes = routeGroupConfigs
        .flatMap((config) =>
          config.childrenRoutes?.filter((cr) => cr.submenuDisplayName && cr.submenuRoute),
        )
        .filter((x) => !!x)

      const currentRoute = routeMatches[routeMatches.length - 1].pathname

      const activeRoute = submenuRoutes.find((r) =>
        `${currentRoute}/`.includes(`${r.submenuRoute!}/`),
      )

      return submenuRoutes?.length ? (
        <DropdownMenu.Root key={routeGroup}>
          <DropdownMenu.Trigger>
            <div className={`tab-nav tab-nav${activeRoute ? '__active' : ''}`}>
              <Text style={{ cursor: 'pointer' }}>{routeGroup}</Text>
            </div>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content className="tab-nav-dropdown" sideOffset={0}>
            <div className="tab-nav-submenu">
              {submenuRoutes.map((cr) => {
                const activeChildRoute = `${currentRoute}/`.includes(`${cr.submenuRoute!}/`)
                return (
                  <DropdownMenu.Item
                    key={cr.submenuDisplayName}
                    className={`tab-nav-submenu__item tab-nav-submenu__item${activeChildRoute ? '--active' : ''}`}
                    onSelect={() => navigate(cr.submenuRoute!, { replace: true })}>
                    <Text weight={activeChildRoute ? 'bold' : 'medium'}>
                      {cr.submenuDisplayName}
                    </Text>
                  </DropdownMenu.Item>
                )
              })}
            </div>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      ) : (
        <div key={routeGroup} className={`tab-nav tab-nav${activeRoute ? '__active' : ''}`}>
          <Text>{routeGroup}</Text>
        </div>
      )
    })
    .filter((x) => !!x)
}
